import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Globe, FileText, Type, Trash2, Eye, RefreshCw, ArrowRight, CheckCircle2, AlertCircle, ExternalLink, Sparkles } from "lucide-react";

/**
 * Dedicated Knowledge Base management page for the public landing agent
 * (the one powering the floating chat bubble on hellosir.ai).
 *
 * Reuses the existing `/api/knowledge/*` endpoints. Because the agent's tenant
 * is also the super admin's tenant, the user context here already has access.
 */
export default function AdminKnowledgeBase() {
  const [settings, setSettings] = useState(null);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);

  // Add URL
  const [url, setUrl] = useState("");
  const [crawlSite, setCrawlSite] = useState(true);
  const [maxPages, setMaxPages] = useState(12);
  const [urlBusy, setUrlBusy] = useState(false);

  // Add text
  const [textName, setTextName] = useState("");
  const [textBody, setTextBody] = useState("");
  const [textBusy, setTextBusy] = useState(false);

  // Add file
  const [file, setFile] = useState(null);
  const [fileBusy, setFileBusy] = useState(false);

  // Preview
  const [preview, setPreview] = useState(null);

  const agentId = settings?.public_landing_agent?.id;

  const loadSources = useCallback(async (id) => {
    if (!id) return;
    try {
      const { data } = await api.get("/knowledge/sources", { params: { agent_id: id } });
      setSources(data || []);
    } catch (e) {
      // Tenant scoping may reject — show empty list with the helper message below.
      setSources([]);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: s } = await api.get("/admin/system-settings");
      setSettings(s);
      if (s?.public_landing_agent?.id) await loadSources(s.public_landing_agent.id);
    } finally {
      setLoading(false);
    }
  }, [loadSources]);

  useEffect(() => { load(); }, [load]);

  const addUrl = async (e) => {
    e?.preventDefault?.();
    if (!url.trim() || !agentId) return;
    setUrlBusy(true);
    try {
      const { data } = await api.post("/knowledge/url", {
        agent_id: agentId,
        url: url.trim(),
        crawl_site: crawlSite,
        max_pages: Math.max(1, Math.min(50, parseInt(maxPages, 10) || 12)),
      });
      toast.success(`Trained on ${data?.pages || "1"} page(s) · ${data?.chunks || ""} chunks`);
      setUrl("");
      await loadSources(agentId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not train on URL");
    } finally {
      setUrlBusy(false);
    }
  };

  const addText = async (e) => {
    e?.preventDefault?.();
    if (!textName.trim() || !textBody.trim() || !agentId) return;
    setTextBusy(true);
    try {
      await api.post("/knowledge/text", { agent_id: agentId, name: textName.trim(), text: textBody });
      toast.success("Text knowledge added.");
      setTextName(""); setTextBody("");
      await loadSources(agentId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add text");
    } finally {
      setTextBusy(false);
    }
  };

  const addFile = async (e) => {
    e?.preventDefault?.();
    if (!file || !agentId) return;
    setFileBusy(true);
    try {
      const fd = new FormData();
      fd.append("agent_id", agentId);
      fd.append("file", file);
      await api.post("/knowledge/file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${file.name}`);
      setFile(null);
      await loadSources(agentId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not upload file");
    } finally {
      setFileBusy(false);
    }
  };

  const removeSource = async (id) => {
    if (!window.confirm("Delete this knowledge source? The agent will forget it immediately.")) return;
    try {
      await api.delete(`/knowledge/source/${id}`);
      toast.success("Source removed");
      await loadSources(agentId);
    } catch (e) {
      toast.error("Could not delete source");
    }
  };

  const previewSource = async (id) => {
    try {
      const { data } = await api.get(`/knowledge/source/${id}/preview`);
      setPreview({ id, ...data });
    } catch (e) {
      toast.error("Could not load preview");
    }
  };

  if (loading) return <div className="text-sm text-[#4B5563]">Loading…</div>;

  // No agent configured yet — point them to /admin/settings.
  if (!settings?.public_landing_agent) {
    return (
      <div className="max-w-2xl rounded-[6px] border border-[#F59E0B]/30 bg-[#FEF3C7] text-[#92400E] p-6" data-testid="no-agent-banner">
        <div className="font-display font-bold text-lg">No landing-page agent selected yet.</div>
        <p className="text-sm mt-2">Pick or create the agent that powers your hellosir.ai chat bubble before training its knowledge base.</p>
        <Link to="/admin/settings" className="btn-primary text-sm inline-flex items-center gap-2 mt-4">
          Open Platform Settings <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  const wired = settings.public_landing_agent;

  return (
    <div data-testid="admin-knowledge-page" className="space-y-8 max-w-5xl">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-extrabold text-3xl">Knowledge base</h1>
          <p className="text-sm text-[#4B5563] mt-1">
            Train the chat bubble on <span className="font-mono">hellosir.ai</span> — agent{" "}
            <span className="font-display font-bold text-[#0F1115]">{wired.name}</span>.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => loadSources(agentId)} className="btn-outline text-sm inline-flex items-center gap-2" data-testid="refresh-kb">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <Link to={`/dashboard/agents/${agentId}`} className="btn-outline text-sm inline-flex items-center gap-2">
            Full agent builder <ExternalLink className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>

      {/* Add sources — three side-by-side cards */}
      <div className="grid lg:grid-cols-3 gap-5">
        {/* URL */}
        <form onSubmit={addUrl} className="rounded-[6px] bg-white border border-[#E2E8F0] p-5 flex flex-col" data-testid="kb-add-url">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-[6px] bg-[#0044FF]/12 text-[#0044FF] flex items-center justify-center"><Globe className="w-5 h-5" /></div>
            <div className="font-display font-bold">From a URL</div>
          </div>
          <p className="text-xs text-[#4B5563] mt-2">Crawl your website, blog or docs.</p>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://hellosir.ai/"
            required
            className="hs-input mt-4"
            data-testid="kb-url-input"
          />
          <label className="flex items-center gap-2 text-sm text-[#374151] mt-3">
            <input type="checkbox" checked={crawlSite} onChange={(e) => setCrawlSite(e.target.checked)} data-testid="kb-url-crawl" />
            Crawl whole site
          </label>
          <label className="text-xs font-mono uppercase tracking-widest text-[#4B5563] mt-3">Max pages</label>
          <input
            type="number"
            min="1" max="50"
            value={maxPages}
            onChange={(e) => setMaxPages(e.target.value)}
            className="hs-input mt-1"
            data-testid="kb-url-maxpages"
          />
          <div className="flex flex-wrap gap-1.5 mt-3">
            {["https://hellosir.ai/", "https://hellosir.ai/use-cases", "https://hellosir.ai/about", "https://hellosir.ai/contact", "https://hellosir.ai/#pricing"].map((u) => (
              <button type="button" key={u} onClick={() => setUrl(u)} className="px-2 py-1 rounded-full bg-[#F3F4F6] hover:bg-[#E5E7EB] text-[10px] font-mono">
                {u.replace("https://hellosir.ai", "") || "/"}
              </button>
            ))}
          </div>
          <button type="submit" disabled={urlBusy || !url.trim()} className="btn-primary text-sm inline-flex items-center justify-center gap-2 mt-auto pt-4">
            {urlBusy ? "Training…" : (<>Train now <ArrowRight className="w-4 h-4" /></>)}
          </button>
        </form>

        {/* File */}
        <form onSubmit={addFile} className="rounded-[6px] bg-white border border-[#E2E8F0] p-5 flex flex-col" data-testid="kb-add-file">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-[6px] bg-[#7C3AED]/12 text-[#7C3AED] flex items-center justify-center"><FileText className="w-5 h-5" /></div>
            <div className="font-display font-bold">From a file</div>
          </div>
          <p className="text-xs text-[#4B5563] mt-2">PDF, DOCX, CSV, MD, TXT, JSON.</p>
          <label className="mt-4 cursor-pointer">
            <div className="border-2 border-dashed border-[#CBD5E1] rounded-[4px] p-4 text-center text-sm text-[#4B5563] hover:border-[#0044FF] hover:bg-[#F0F4FF] transition-colors">
              {file ? <><span className="font-mono">{file.name}</span><br /><span className="text-[11px]">{(file.size / 1024).toFixed(1)} KB · click to change</span></> : "Click to choose a file"}
            </div>
            <input
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              accept=".pdf,.docx,.csv,.md,.txt,.json"
              className="hidden"
              data-testid="kb-file-input"
            />
          </label>
          <button type="submit" disabled={fileBusy || !file} className="btn-primary text-sm inline-flex items-center justify-center gap-2 mt-auto pt-4">
            {fileBusy ? "Uploading…" : (<>Upload &amp; index <ArrowRight className="w-4 h-4" /></>)}
          </button>
        </form>

        {/* Text */}
        <form onSubmit={addText} className="rounded-[6px] bg-white border border-[#E2E8F0] p-5 flex flex-col" data-testid="kb-add-text">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-[6px] bg-[#10B981]/12 text-[#10B981] flex items-center justify-center"><Type className="w-5 h-5" /></div>
            <div className="font-display font-bold">Raw text</div>
          </div>
          <p className="text-xs text-[#4B5563] mt-2">Paste FAQs, policies or notes directly.</p>
          <input
            type="text"
            value={textName}
            onChange={(e) => setTextName(e.target.value)}
            placeholder="Title (e.g. Refund FAQ)"
            className="hs-input mt-4"
            data-testid="kb-text-name"
          />
          <textarea
            value={textBody}
            onChange={(e) => setTextBody(e.target.value)}
            rows={6}
            placeholder="Paste content here…"
            className="hs-input mt-2 font-mono text-xs"
            data-testid="kb-text-body"
          />
          <button type="submit" disabled={textBusy || !textName.trim() || !textBody.trim()} className="btn-primary text-sm inline-flex items-center justify-center gap-2 mt-auto pt-4">
            {textBusy ? "Saving…" : (<>Add to KB <ArrowRight className="w-4 h-4" /></>)}
          </button>
        </form>
      </div>

      {/* Source list */}
      <section>
        <div className="flex items-end justify-between gap-4 mb-4">
          <div>
            <h2 className="font-display font-bold text-2xl">Trained sources</h2>
            <p className="text-sm text-[#4B5563] mt-1">{sources.length} source{sources.length === 1 ? "" : "s"} feeding the bubble.</p>
          </div>
        </div>

        {sources.length === 0 ? (
          <div className="rounded-[6px] border-2 border-dashed border-[#CBD5E1] p-10 text-center" data-testid="kb-empty">
            <Sparkles className="w-10 h-10 text-[#94A3B8] mx-auto" />
            <div className="font-display font-bold text-lg mt-4">No knowledge yet</div>
            <p className="text-sm text-[#4B5563] mt-2">Use one of the cards above to train your bubble. Start with <span className="font-mono">https://hellosir.ai/</span> + "Crawl whole site".</p>
          </div>
        ) : (
          <div className="rounded-[6px] bg-white border border-[#E2E8F0] divide-y divide-[#E2E8F0] overflow-hidden">
            {sources.map((s) => (
              <div key={s.id} className="p-4 flex flex-wrap items-center gap-4" data-testid={`kb-source-${s.id}`}>
                <SourceIcon type={s.type} />
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-sm truncate">{s.name}</div>
                  <div className="text-[11px] font-mono text-[#4B5563] mt-0.5 flex items-center gap-3 flex-wrap">
                    <span className="uppercase">{s.type}</span>
                    <span>{s.chunks ?? 0} chunks</span>
                    <StatusPill status={s.status} />
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => previewSource(s.id)}
                    className="p-2 rounded hover:bg-[#F3F4F6]"
                    title="Preview extracted content"
                    data-testid={`kb-preview-${s.id}`}
                  >
                    <Eye className="w-4 h-4 text-[#4B5563]" />
                  </button>
                  <button
                    onClick={() => removeSource(s.id)}
                    className="p-2 rounded hover:bg-[#FEE2E2] text-[#EF4444]"
                    title="Delete"
                    data-testid={`kb-delete-${s.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Preview modal */}
      {preview && (
        <div
          className="fixed inset-0 z-[70] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setPreview(null)}
          data-testid="kb-preview-modal"
        >
          <div className="bg-white rounded-[6px] w-full max-w-3xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-[#E2E8F0] flex items-center justify-between">
              <div className="min-w-0">
                <div className="font-display font-bold text-lg truncate">{preview.name || "Preview"}</div>
                <div className="text-[11px] font-mono text-[#4B5563] mt-0.5">{preview.chunks ?? 0} chunks indexed</div>
              </div>
              <button onClick={() => setPreview(null)} className="text-2xl text-[#4B5563] hover:text-[#0F1115]">&times;</button>
            </div>
            <div className="p-5 overflow-y-auto">
              <pre className="text-xs font-mono whitespace-pre-wrap text-[#0F1115] leading-relaxed">{preview.text || preview.content || "(empty)"}</pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SourceIcon({ type }) {
  const map = {
    url:  { Icon: Globe,    color: "#0044FF" },
    file: { Icon: FileText, color: "#7C3AED" },
    text: { Icon: Type,     color: "#10B981" },
  };
  const { Icon, color } = map[type] || map.text;
  return (
    <div className="w-10 h-10 rounded-[6px] flex items-center justify-center shrink-0" style={{ background: `${color}14`, color }}>
      <Icon className="w-5 h-5" />
    </div>
  );
}

function StatusPill({ status }) {
  const ok = status === "trained" || status === "ready" || status === "indexed";
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-mono ${ok ? "bg-[#10B981]/12 text-[#065F46]" : "bg-[#FEF3C7] text-[#92400E]"}`}>
      {ok ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
      {status || "pending"}
    </span>
  );
}
